/**
 * The Today page's day from the server, and the garden's own picture for it.
 *
 * See the backend's routes/today.js. Everything here is about the season and
 * the calendar, not the forecast: the season's name in the gardener's
 * climate and its painting, the frost and freeze dates, the station's hottest
 * and coldest stretch and wettest and driest month, and what the planner
 * says about the week. The forecast's jobs come from the plant outlook.
 *
 * Endpoints:
 * - GET    /today?zip= → TodayDay, for the garden's own day (the server's,
 *   from the garden's time zone; architecture audit A31: no day is sent).
 * - PUT    /today/photo (multipart: 'photo') → { success, photo }.
 * - DELETE /today/photo.
 *
 * Who uses it: features/today (TodayPage, TodayHeader, TodayPhotoDialog,
 * TodayPictureSetting, paintings, todayCalendar), features/season
 * (SeasonHeader, seasonReview, useSeasonFrost) and features/welcome.
 * Caching: [TODAY_QUERY_KEY, zip, date] ('today'), QUERY_STALE_TIME_MS;
 * keyed by the garden's postcode and its own day as the garden place says
 * them (shared/hooks/useGardenPostcode), so the page turns at the garden's
 * midnight, and invalidated when the picture changes. Auth: the signed-in
 * token, in the garden being tended.
 * In a shared garden no ?zip is sent (gardenMembers.gardenZip), so the
 * backend reads the garden at its owner's postcode.
 *
 * @module api/today
 */

import { apiClient } from './apiClient';
import { gardenZip } from './gardenMembers';

/** A frost season's three dates, "MM-DD": 1 year in 10, typical, 9 years in 10. */
export interface FrostWindow {
  early: string | null;
  typical: string | null;
  late: string | null;
}

/** The stretch of the year at a normal's extreme: its first and last day, "MM-DD". */
export interface NormalStretch {
  start: string;
  end: string;
  /** The normal high (hottest) or low (coldest), in Fahrenheit. */
  value: number;
}

/** A month and its normal rain. */
export interface RainMonth {
  /** 1 to 12. */
  month: number;
  inches: number;
}

/** The four seasons, as the server reduces the planner's twelve to them. */
export type FourSeason = 'spring' | 'summer' | 'fall' | 'winter';

/** What GET /api/today answers. */
export interface TodayDay {
  /** The server's flag. */
  success: boolean;
  /** The day asked about, "YYYY-MM-DD". */
  date: string;
  /**
   * The planner's season for this month in the garden's climate, e.g. early_fall, "Early Fall", and which of the
   * four seasons it is (four).
   */
  season: { key: string; name: string; four: FourSeason | null } | null;
  /**
   * The seasonal painting for it: its name (fall_early), where it is, and where the strip across the top of Today crops it
   * (the Grow setting today.STRIP_<key>, else today.PAINTING_<key>): the vertical part of object-position, 0 the top to 100
   * the bottom; null when the server has none.
   */
  painting: { key: string; url: string; focus: number | null };
  /** The garden's own picture, in place of the painting, when it has one. */
  photo: { url: string } | null;
  /** The station's frost and freeze dates; null where it has none. */
  frost: {
    lastFrost: FrostWindow | null;
    firstFrost: FrostWindow | null;
    lastFreeze: FrostWindow | null;
    firstFreeze: FrostWindow | null;
  } | null;
  /** The hottest and coldest stretch of the station's normal year. */
  stretches: { hottest: NormalStretch | null; coldest: NormalStretch | null } | null;
  /** The station's wettest and driest month. */
  rain: { wettest: RainMonth; driest: RainMonth } | null;
  /** The planner's general notes for this week, whole, as written; textEs is the Spanish, null where none is written yet. */
  planner: Array<{ id: number; text: string; textEs?: string | null }>;
  /** The planner week those notes are for (1-52), for "From your planner, week 40". */
  plannerWeek: number;
}

/**
 * The day's season, calendar and planner notes for a garden, on the garden's
 * own day (the server's).
 *
 * @param zip - The garden's postcode (useGardenPostcode); sent only in the person's own garden (gardenZip), and the
 *   server reads the garden's saved one without it.
 * @returns The day.
 */
export async function getToday(zip: string): Promise<TodayDay> {
  const response = await apiClient.get<TodayDay>('/today', { params: { zip: gardenZip(zip) } });
  return response.data;
}

/**
 * Puts the garden's own picture at the top of Today, in place of the season's painting.
 *
 * @param photo - The picture, already shrunk by the photo processor.
 * @returns Its new address.
 */
export async function putTodayPhoto(photo: File | Blob): Promise<{ url: string } | null> {
  const data = new FormData();
  data.append('photo', photo);
  const response = await apiClient.put<{ success: boolean; photo: { url: string } | null }>('/today/photo', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.photo;
}

/** Goes back to the season's painting. */
export async function deleteTodayPhoto(): Promise<void> {
  await apiClient.delete('/today/photo');
}
